/**
 * Probe's addresses inside Apps, as pure helpers for the sidebar's
 * selection (mirroring appsPaths and homePaths):
 *
 *   /$org/apps/probe                      the stage: start an interview
 *   /$org/apps/probe/practice             Home: the areas, actions and interviews
 *   /$org/apps/probe/interviews/<id>      one interview: review + transcript
 *   /$org/apps/probe/briefing             what the examiner has read
 *   /$org/apps/probe/interviews           the old All interviews, redirected to Home
 *
 * @module components/apps/probe/probePaths
 */
import { appAreaFromPathname } from "../appsPaths";

export type ProbeSection =
  | { readonly kind: "stage" }
  | { readonly kind: "interviews" }
  | { readonly kind: "interview"; readonly interviewId: string }
  | { readonly kind: "briefing" }
  | { readonly kind: "practice" };

/** Where in Probe a pathname is, or null outside Probe (or at an address it does not have). */
export function probeSectionFromPathname(pathname: string): ProbeSection | null {
  if (appAreaFromPathname(pathname) !== "probe") return null;
  const rest = pathname.split("/").filter(Boolean).slice(3);
  if (rest.length === 0) return { kind: "stage" };
  const [first, second, ...extra] = rest;
  if (extra.length > 0) return null;
  if (first === "interviews") {
    if (second === undefined) return { kind: "interviews" };
    return { kind: "interview", interviewId: safeDecode(second) };
  }
  if (second !== undefined) return null;
  if (first === "briefing") return { kind: "briefing" };
  if (first === "practice") return { kind: "practice" };
  return null;
}

const safeDecode = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};
